import { Form, Row, Col, Button, Collapse } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import * as Icon from 'react-bootstrap-icons'
import { useState } from 'react'
import useInit from './hooks/useInit'
import filterableDeviceAttrsProps from './utils/filterableDeviceAttrsProps'
import submitFilterForm from './utils/submitFilterForm'
import useDynamicFilter from 'hooks/useDynamicFilter'
import SelectWrapper from 'shared/components/SelectWrapper'
import { roles, deviceCreateStatuses, deviceCreateTypes } from 'consts'
import exportData from './utils/exportData'
import hasAnyRole from 'shared/utils/hasAnyRole'
import handleFiltersSave from './utils/handleFiltersSave'

interface DevicesFilterProps {
  handleFilter: Function
  setShowOverlay: Function
  currentCustomer: {} | null
  filterVisible: boolean
}

const DevicesFilter: React.FunctionComponent<DevicesFilterProps> = ({
  handleFilter,
  setShowOverlay,
  currentCustomer,
  filterVisible,
}: DevicesFilterProps) => {
  const [canReset, setCanReset] = useState<boolean>(false)
  const [canSearch, setCanSearch] = useState<boolean>(false)
  const [canSearchAll, setCanSearchAll] = useState<boolean>(true)
  const [centers, setCenters] = useState<any>(false)
  const [allCenters, setAllCenters] = useState<any>(false)
  const [sites, setSites] = useState<any>(false)
  const [manufacturers, setManufacturers] = useState<any>(false)
  const [models, setModels] = useState<any>(false)
  const [allModels, setAllModels] = useState<any>(false)
  const [types, setTypes] = useState<any>(false)
  const [serviceStatuses, setServiceStatuses] = useState<any>(false)
  const [deviceUsages, setDeviceUsages] = useState<any>(false)
  const [warrantyStatuses, setWarrantyStatuses] = useState<any>(false)

  const [currentSite, setCurrentSite] = useState<any>(null)
  const [currentManufacturer, setCurrentManufacturer] = useState<any>(null)

  const [activeFiltersCount, setActiveFiltersCount] = useState<Number>(0)
  const [searchAllCustomers, setSearchAllCustomers] = useState<boolean>(false)

  const [currentFilter, setCurrentFilter] = useState<any>({})
  const [currentFilterRawData, setCurrentFilterRawData] = useState<any>({})

  const { control, handleSubmit, reset, setValue, getValues } = useForm({})

  const [isSaveFiltersRequestLoading, setIsSaveFiltersRequestLoading] = useState<boolean>(false)
  const [isExportRequestLoading, setIsExportRequestLoading] = useState<boolean>(false)

  const isSuperAdmin = hasAnyRole([roles.superAdmin])
  const isAdmin = hasAnyRole([roles.admin])

  const filters: Array<filterableDeviceAttrsProps> = [
    {
      name: 'site_id',
      operator: '=',
      dataType: Number,
      data: sites,
      label: 'Location',
      allDataLabel: 'All locations',
      onChange: (e) => {
        setCurrentSite(e?.id)
      },
      isMulti: false,
    },
    {
      name: 'center_id',
      operator: '=',
      dataType: Number,
      data: centers,
      label: 'Cost Center',
      allDataLabel: 'All cost centers',
      isMulti: false,
    },
    {
      name: 'manufacturer_id',
      operator: '=',
      dataType: Number,
      data: manufacturers,
      label: 'Manufacturer',
      allDataLabel: 'All manufacturers',
      onChange: (e) => setCurrentManufacturer(e?.id),
      isMulti: false,
    },
    {
      name: 'model_id',
      operator: '=',
      dataType: Number,
      data: models,
      label: 'Model',
      allDataLabel: 'All models',
      isMulti: false,
    },
    {
      name: 'type_id',
      operator: '=',
      dataType: Number,
      data: types,
      label: 'Type',
      allDataLabel: 'All types',
      isMulti: false,
    },
    {
      name: 'warranty_status_id',
      operator: '=',
      dataType: Number,
      data: warrantyStatuses,
      label: 'Warranty Status',
      allDataLabel: 'All warranty statuses',
      isMulti: false,
    },
    {
      name: 'service_status_id',
      operator: 'in',
      dataType: Number,
      data: serviceStatuses,
      label: 'Service Status',
      allDataLabel: 'All service statuses',
      isMulti: true,
    },
    {
      name: 'device_usage_id',
      operator: '=',
      dataType: Number,
      data: deviceUsages,
      label: 'Device Usage',
      allDataLabel: 'All device usages',
      isMulti: false,
    },
    {
      name: 'create_status',
      operator: '=',
      dataType: Number,
      data: deviceCreateStatuses,
      label: 'Create Status',
      allDataLabel: 'All create statuses',
      isMulti: false,
    },
    {
      name: 'create_type',
      operator: '=',
      dataType: Number,
      data: deviceCreateTypes,
      label: 'Create Type',
      allDataLabel: 'All create types',
      isMulti: false,
    },
  ]

  const onSubmit = (data) => {
    setShowOverlay(true)
    setCanReset(true)
    if (searchAllCustomers) {
      setCanSearch(true)
      setCanSearchAll(false)
    } else {
      setCanSearch(false)
      setCanSearchAll(true)
    }
    submitFilterForm({
      data,
      filters,
      handleFilter,
      setActiveFiltersCount,
      searchAllCustomers,
      setCurrentFilter,
      setCurrentFilterRawData,
    })
  }

  const onInputChange = () => {
    setCanReset(true)
    setCanSearch(true)
    setCanSearchAll(true)
  }

  useInit({
    setManufacturers,
    setModels,
    setAllModels,
    setTypes,
    setServiceStatuses,
    setDeviceUsages,
    setWarrantyStatuses,
    setValue,
    handleFilterSubmit: handleSubmit(onSubmit),
    currentCustomer,
    setCenters,
    setAllCenters,
    setSites,
    setCurrentFilterRawData,
    setCurrentFilter,
    filters,
    reset,
    setCurrentSite,
    setCurrentManufacturer,
  })

  useDynamicFilter({
    allRows: allCenters,
    setFilteredRows: setCenters,
    filterColumnName: 'site_id',
    inputValue: currentSite,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'center_id',
    currentFormValues: getValues(),
    identityColumnName: 'id',
    isFormFieldValueNameObject: true,
  })

  useDynamicFilter({
    allRows: allModels,
    setFilteredRows: setModels,
    filterColumnName: 'manufacturer_id',
    inputValue: currentManufacturer,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'model_id',
    currentFormValues: getValues(),
    identityColumnName: 'id',
    isFormFieldValueNameObject: true,
  })

  return (
    <>
      <div className='d-flex flex-column'>
        <Collapse in={filterVisible}>
          <div className='card-header position-relative devices-filter'>
            <Form onSubmit={handleSubmit(onSubmit)} id='filter-devices-form'>
              <Row>
                {filters.map((filter: filterableDeviceAttrsProps, index: number) => {
                  return (
                    <Col md='3' key={index}>
                      <Form.Group className='form-group'>
                        <Form.Label>{filter.label}</Form.Label>
                        <SelectWrapper
                          name={filter.name}
                          isMulti={filter.isMulti}
                          control={control}
                          options={filter.data}
                          optionLabel='name'
                          optionValue='id'
                          defaultValue={''}
                          placeholder={filter.allDataLabel}
                          onChange={(e) => {
                            onInputChange()
                            if (filter.onChange) {
                              filter.onChange(e)
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  )
                })}
              </Row>
              <Row className='filter-buttons'>
                <Col>
                  <Button
                    disabled={isSaveFiltersRequestLoading}
                    onClick={() => {
                      handleFiltersSave({
                        data: currentFilterRawData,
                        setShowOverlay,
                        setIsSaveFiltersRequestLoading,
                      })
                    }}
                    variant='primary'
                    size='sm'
                    type='reset'
                    className='float-left btn-margin'
                  >
                    {isSaveFiltersRequestLoading ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        &nbsp;
                      </>
                    ) : (
                      <Icon.Save />
                    )}
                    &nbsp; Save Active Filters
                  </Button>
                  {(isSuperAdmin || isAdmin) && (
                    <Button
                      disabled={isExportRequestLoading}
                      variant='primary'
                      onClick={() => {
                        exportData({
                          filter: currentFilter,
                          setIsExportRequestLoading,
                          setShowOverlay,
                        })
                      }}
                      size='sm'
                      name='submit3'
                      className='float-left btn-margin'
                    >
                      {isExportRequestLoading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          &nbsp;
                        </>
                      ) : (
                        <Icon.FiletypeXlsx />
                      )}
                      &nbsp; Excel Export
                    </Button>
                  )}

                  {isSuperAdmin && (
                    <Button
                      disabled={!canSearchAll}
                      variant='primary'
                      onClick={() => setSearchAllCustomers(true)}
                      size='sm'
                      type='submit'
                      name='submit1'
                      className='float-right'
                    >
                      <Icon.Search />
                      &nbsp; Search for All
                    </Button>
                  )}

                  <Button
                    disabled={!canSearch}
                    variant='primary'
                    onClick={() => setSearchAllCustomers(false)}
                    size='sm'
                    type='submit'
                    name='submit2'
                    className='float-right btn-margin'
                  >
                    <Icon.Search />
                    &nbsp; Search
                  </Button>

                  <Button
                    disabled={!canReset}
                    onClick={() => {
                      setShowOverlay(true)
                      setCanReset(false)
                      setCanSearch(false)
                      setCanSearchAll(true)
                      reset()
                      filters.forEach((value) => setValue(value.name, null))
                      setCenters(allCenters)
                      setModels(allModels)
                      setCurrentFilterRawData({})
                      setCurrentFilter({})
                      handleFilter({})
                      setActiveFiltersCount(0)
                    }}
                    variant='primary'
                    size='sm'
                    type='reset'
                    className='float-right btn-margin'
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </Collapse>
      </div>
    </>
  )
}

export default DevicesFilter
